/**
 * Desktop MANAGE PARTY (DESIGN-CHANGELOG 2026-09-03 "MANAGE PARTY is its own
 * full-screen page"; `design/GT Crew.dc.html:228-362`). AA phase 5 §K.
 *
 * Leaders only: the PARTY page's entry row is rendered for a leader alone,
 * and `headerVM` sends a player who lands on `manage` to the PARTY page. The
 * panels are the ones the PARTY screen used to carry inline — MANAGE PARTY
 * (formed / members / URL, PARTY NAME + RENAME), JOIN LINK (the public link
 * and the requests it produced), EMAIL INVITES, MEMBERS, LEADER CONTROLS —
 * moved here whole so no write path changed; the PARTY page keeps the
 * roster cards. Full-screen: no app header, `◂ BACK TO PARTY` is the way out.
 */

import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import { Avatar } from '../../components/Avatar.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { useQd } from '../../store/index.ts';
import { crewVM, ownerVM } from '../../store/selectors.ts';
import InvitePanel from '../modals/InvitePanel.tsx';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: MONO,
  fontSize: '11.5px',
  padding: '10px 12px',
  outline: 'none',
};

/** Admin-row action chips (template lines 685-687). */
const CHIP: CSSProperties = {
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text-3)',
  fontFamily: MONO,
  fontSize: '10px',
  padding: '6px 9px',
  cursor: 'pointer',
};

export default function Manage(): JSX.Element {
  const s = useQd();
  const v = crewVM(s);

  useEffect(() => {
    if (v.meAdmin) {
      void s.loadPending();
      void s.loadJoinLink();
      void s.loadJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.meAdmin]);

  return (
    <div
      data-screen-label="Manage party"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        padding: '22px 26px 40px',
        maxWidth: '1180px',
        width: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div
          onClick={() => s.nav('crew')}
          className="d-hov-acc"
          style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: 10, letterSpacing: '.05em', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          ◂ BACK TO PARTY
        </div>
        <span style={{ fontFamily: PIXEL, fontSize: 13, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>MANAGE PARTY</span>
      </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            border: '1px solid var(--gt-border)',
            background: 'var(--gt-panel)',
            padding: '18px 18px 22px',
          }}
        >
          {/* `MANAGE PARTY — <NAME>` with the role you hold beside it, and the
              crew's own line beneath: the mirror's header row, which names the
              crew because an account can be in several and this panel only ever
              manages the active one. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '8px', color: 'var(--gt-text)' }}>
              MANAGE PARTY — {v.crewNameUp}
            </span>
            <div style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: MONO,
                fontSize: '8px',
                letterSpacing: '.06em',
                padding: '3px 7px',
                border: '1px solid ' + v.myRoleClr,
                color: v.myRoleClr,
                whiteSpace: 'nowrap',
              }}
            >
              YOU — {v.myRoleLbl}
            </span>
          </div>
          <div
            style={{
              marginTop: -8,
              fontFamily: MONO,
              fontSize: '9.5px',
              letterSpacing: '.04em',
              color: 'var(--gt-muted-2)',
            }}
          >
            {v.crewMetaTxt}
          </div>

          {/* CREW NAME — first in the block, owner-only (3030). Twin of the
              phone screen; see it for why the SLUG does not move with the name.
              Not in the mirrors; a DESIGN-CHANGELOG entry is owed. */}
          {v.canRename ? (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span
                style={{
                  fontFamily: MONO,
                  fontSize: '9px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted-2)',
                }}
              >
                PARTY NAME
              </span>
              <input
                value={v.crewNameDraft}
                onChange={(e) => s.setCrewNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.repeat) void s.renameCrew();
                }}
                maxLength={40}
                placeholder="PARTY NAME"
                style={{ ...FIELD, flex: 1, minWidth: '200px', maxWidth: '300px' }}
              />
              <PixelButton
                label={v.renameLbl}
                variant="outline"
                busy={v.renameBusy}
                disabled={v.renameLocked || !v.renameDirty}
                onClick={() => void s.renameCrew()}
                fs={8}
                pad="12px 14px"
              />
            </div>
          ) : null}

          {/* JOIN LINK — the shareable door (3056, docs/JOIN-LINKS.md). Above
              REQUESTS because it is what produces them. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: '7px',
                letterSpacing: '.08em',
                color: 'var(--gt-muted)',
              }}
            >
              {v.jlHdr}
            </div>
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9px',
                lineHeight: 1.8,
                letterSpacing: '.05em',
                color: 'var(--gt-muted-2)',
              }}
            >
              {v.jlLive ? v.jlOnTxt : v.jlOffTxt}
            </span>
            {/* THE URL EXISTS FOR ONE RENDER. The server keeps only a hash and
                genuinely cannot return it again, so there is no "show me the
                link" affordance to build — rotating is the only way back. */}
            {/* THE LINK ITSELF, shown whenever there is one (3057). It used to
                appear only on the render that minted it, which meant the only
                way to share it a second time was NEW LINK — killing the copy
                already sitting in the chat it was posted to. */}
            {v.jlUrl ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '7px',
                  border: '1px dashed var(--gt-border-2)',
                  padding: '11px 12px',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: '10px',
                    color: 'var(--gt-text)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {v.jlUrl}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span
                    onClick={() => void navigator.clipboard?.writeText(v.jlUrl)}
                    style={CHIP}
                  >
                    COPY
                  </span>
                </div>
              </div>
            ) : v.jlUnreadable ? (
              /* Live, but minted before the token was stored. Say why rather
                 than showing a blank space where a link should be. */
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9px',
                  lineHeight: 1.8,
                  letterSpacing: '.05em',
                  color: 'var(--acc2)',
                }}
              >
                {v.jlUnreadableTxt}
              </span>
            ) : null}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <PixelButton
                label={v.jlNewLbl}
                busy={v.jlBusy}
                onClick={() => void s.newJoinLink()}
                fs={8}
                pad="14px 14px"
                nowrap
              />
              {v.jlLive ? (
                <span
                  onClick={() => {
                    if (!v.jlBusy) void s.offJoinLink();
                  }}
                  style={{ ...CHIP, color: 'var(--acc2)' }}
                >
                  {v.jlOffLbl}
                </span>
              ) : null}
            </div>
            {v.jlLive ? (
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9px',
                  lineHeight: 1.8,
                  letterSpacing: '.05em',
                  color: 'var(--gt-muted-2)',
                }}
              >
                {v.jlRotateWarn}
              </span>
            ) : null}
          </div>

          {/* REQUESTS — above PENDING and outside every count, for the reason
              PENDING is: Marcus's "invites should not count as joined" applies
              identically to somebody who has only asked. */}
          {v.hasRequests ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: '7px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted)',
                }}
              >
                {v.requestsHdr}
              </div>
              {v.requestRows.map((r) => (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px',
                    border: '1px dashed var(--gt-border-2)',
                    padding: '11px 12px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '11px',
                      color: 'var(--gt-text)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {r.name}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '9px',
                      letterSpacing: '.05em',
                      color: 'var(--gt-muted)',
                    }}
                  >
                    {r.whenTxt}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span
                      onClick={() => {
                        if (!r.busy) void s.decideRequest(r.id, 'approve');
                      }}
                      style={{
                        ...CHIP,
                        ...(r.busy ? { color: 'var(--gt-muted)', cursor: 'progress' } : null),
                      }}
                    >
                      {r.approveLbl}
                    </span>
                    <span
                      onClick={() => {
                        if (!r.busy) void s.decideRequest(r.id, 'deny');
                      }}
                      style={{
                        ...CHIP,
                        color: 'var(--acc2)',
                        ...(r.busy ? { cursor: 'progress' } : null),
                      }}
                    >
                      {r.denyLbl}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* ONE FIELD, on one line (3026, docs/INVITES.md). Twin of the phone
              form: the NAME box is gone because picking somebody else's display
              name was never ours to do, and the temp password went with it
              because no account exists until they accept.

              The mirrors still draw NAME and `EMAIL (OPTIONAL)`; a
              DESIGN-CHANGELOG entry is owed upstream for that and for this
              (X-HANDOFF §2.5, docs/INVITES.md §5). */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              value={v.invEmail}
              onChange={(e) => s.setInvEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.repeat) void s.sendInvite();
              }}
              type="email"
              placeholder="EMAIL ADDRESS"
              style={{ ...FIELD, flex: 1, minWidth: '200px', maxWidth: '300px' }}
            />
            <PixelButton
              label={v.inviteLbl}
              busy={v.invBusy}
              onClick={() => void s.sendInvite()}
              fs={8}
              pad="12px 14px"
            />
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9px',
                lineHeight: 1.8,
                letterSpacing: '.05em',
                color: 'var(--gt-muted-2)',
                flexBasis: '100%',
              }}
            >
              {v.inviteNote}
            </span>
          </div>

          {/* PENDING — outside every count, because an invitation is not a
              membership. Expired rows stay and RESEND is the way out. */}
          {v.hasPending ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: '7.5px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted)',
                }}
              >
                {v.pendingHdr}
              </div>
              {v.pendingRows.map((p) => (
                <div
                  key={p.key}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    border: '1px dashed var(--gt-border-2)',
                    padding: '11px 13px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '11.5px',
                      color: 'var(--gt-text)',
                      minWidth: '180px',
                    }}
                  >
                    {p.email}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '9px',
                      letterSpacing: '.05em',
                      color: p.clr,
                      flex: 1,
                      minWidth: '180px',
                    }}
                  >
                    {p.whenTxt} · {p.deliveryTxt}
                  </span>
                  <span
                    onClick={() => {
                      if (!p.busy) void s.sendInvite(p.email);
                    }}
                    style={{
                      ...CHIP,
                      ...(p.busy ? { color: 'var(--gt-muted)', cursor: 'progress' } : null),
                    }}
                  >
                    {p.resendLbl}
                  </span>
                  <span
                    onClick={() => {
                      if (!p.busy) void s.cancelInvite(p.email);
                    }}
                    style={{
                      ...CHIP,
                      color: 'var(--acc2)',
                      ...(p.busy ? { cursor: 'progress' } : null),
                    }}
                  >
                    CANCEL
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {v.hasInvite ? <InvitePanel /> : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {v.adminRows.map((u) => (
              <div
                key={u.key}
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  border: '1px solid var(--gt-line)',
                  background: 'var(--gt-card)',
                  padding: '9px 12px',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', minWidth: '170px' }}>
                  <Avatar size={30} fs={8} ini={u.ini} bg={u.hueBg} hasAv={u.hasAv} avBg={u.avBg} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--gt-text)' }}>{u.name}</span>
                    {/* The LOGIN address (3029), or the warning in the alert
                        tone when the account still has the synthesized
                        placeholder — see the phone twin. */}
                    <span style={{ fontFamily: MONO, fontSize: '9.5px', color: u.emailClr }}>
                      {u.emailTxt}
                    </span>
                  </div>
                </div>
                {/* Read-only role chip — see the phone screen for why the
                    select had to go rather than be repaired. */}
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: '10px',
                    letterSpacing: '.04em',
                    color: u.roleClr,
                    border: '1px solid ' + u.roleBd,
                    padding: '6px 9px',
                  }}
                >
                  {u.roleTxt}
                </span>
                <span style={{ fontFamily: MONO, fontSize: '9.5px', color: u.statusClr, minWidth: '80px' }}>
                  {u.statusTxt}
                </span>
                <div style={{ flex: 1 }} />
                {/* COPY LINK and RESET PW are gone — see the phone twin for
                    the reasoning. Both were temp-password-world controls, and
                    RESET PW had begun rendering for exactly the members the
                    server refuses. */}
                {u.canRm ? (
                  <span
                    onClick={() => {
                      if (s.rmArm === u.key) void s.doRm(u.key);
                      else s.setAdminForm({ rmArm: u.key });
                    }}
                    style={{ ...CHIP, border: '1px solid ' + u.rmBd, color: u.rmClr }}
                  >
                    {u.rmTxt}
                  </span>
                ) : null}
                {/* D-03: the code is shown once, so COPY INVITE carries identity
                    only. `width:100%` puts the note on its own wrapped line. */}
              </div>
            ))}
          </div>
        </div>
      {ownerVM(s).show ? <OwnerControls /> : null}
    </div>
  );
}

/**
 * OWNER CONTROLS. `design/GT Crew.dc.html` lines 270-291 — two columns split by
 * a hairline, each a button over its consequence line.
 *
 * The mirror's trailing `✓ {{ trMsg }}` row is NOT ported: it is inside this
 * same owner gate, and a transfer demotes you, so the panel unmounts in the
 * same commit that would set the message. `handOver` toasts instead.
 */
function OwnerControls(): JSX.Element {
  const s = useQd();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border)',
        background: 'var(--gt-panel)',
      }}
    >
      <div
        style={{
          background: 'var(--gt-raised)',
          borderBottom: '1px solid var(--gt-border)',
          padding: '11px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontFamily: PIXEL, fontSize: '8px', color: 'var(--gt-gold)', transform: 'translateY(0.13em)' }}>
          LEADER CONTROLS
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: MONO, fontSize: '9px', color: 'var(--gt-muted-2)' }}>
          ONLY THE LEADER SEES THIS PANEL
        </span>
      </div>

      <div style={{ display: 'flex', gap: 20, padding: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minWidth: 250 }}>
          <span
            onClick={() => s.openOwn('transfer')}
            style={{ ...OWN_BTN, borderColor: 'var(--gt-border-2)', color: 'var(--gt-text-3)' }}
          >
            HAND OVER THE PARTY ▸
          </span>
          <span style={OWN_NOTE}>
            HANDS OVER EVERYTHING — YOU BECOME A REGULAR PLAYER. EXACTLY ONE LEADER AT ALL TIMES.
          </span>
        </div>

        <div style={{ width: 1, background: 'var(--gt-line)', alignSelf: 'stretch' }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: 1, minWidth: 250 }}>
          <span
            onClick={() => s.openOwn('delete')}
            style={{ ...OWN_BTN, borderColor: 'var(--acc2)', color: 'var(--acc2)' }}
          >
            DISBAND PARTY ✕
          </span>
          <span style={OWN_NOTE}>
            REMOVES THE BOARD, CALLS, BALLOTS, VIDEOS AND EVERY MEMBERSHIP. ACCOUNTS SURVIVE. OLD
            INVITE LINKS DIE — THE SLUG IS NOT RECYCLED.
          </span>
        </div>
      </div>
    </div>
  );
}

const OWN_BTN: CSSProperties = {
  alignSelf: 'flex-start',
  border: '1px solid',
  fontFamily: MONO,
  fontSize: '10px',
  padding: '9px 12px',
  cursor: 'pointer',
};

const OWN_NOTE: CSSProperties = {
  fontFamily: MONO,
  fontSize: '8.5px',
  lineHeight: 1.7,
  letterSpacing: '.04em',
  color: 'var(--gt-muted-2)',
};
